/**
 * RecentActivity — compact status-event feed. Grouped by day (TODAY, etc.).
 */
import { StatusPill } from "./StatusPill";

function dayLabel(iso, today) {
    const d = new Date(iso);
    const t = today ?? new Date();
    if (d.toDateString() === t.toDateString()) return "TODAY";
    const yest = new Date(t); yest.setDate(t.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "YESTERDAY";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }).toUpperCase();
}

function timeLabel(iso) {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

export default function RecentActivity({ events, milestones }) {
    const titleByCode = new Map(milestones.map((m) => [m.code, m.title]));
    const grouped = new Map();
    for (const e of events) {
        const key = dayLabel(e.created_at);
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(e);
    }
    if (events.length === 0) {
        return (
            <div data-testid="gtm-recent-activity-empty" className="qoryn-panel rounded-2xl p-5">
                <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">Recent Activity</div>
                <div className="mt-2 text-[12.5px] text-qoryn-textDim">No status transitions yet.</div>
            </div>
        );
    }
    return (
        <div data-testid="gtm-recent-activity" className="qoryn-panel rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-[15px] text-qoryn-text tracking-tight">Recent Activity</h3>
                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    {events.length} events
                </span>
            </div>
            <div className="space-y-4">
                {[...grouped.entries()].map(([day, list]) => (
                    <div key={day} data-testid={`gtm-activity-group-${day.replace(/\s/g, "_")}`}>
                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted mb-2">{day}</div>
                        <ul className="space-y-2">
                            {list.map((e) => (
                                <li key={e.id} data-testid={`gtm-activity-event-${e.id}`} className="flex items-center gap-3 min-w-0">
                                    <span className="w-14 shrink-0 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                        {timeLabel(e.created_at)}
                                    </span>
                                    <span className="flex-1 min-w-0 text-[12.5px] text-qoryn-text truncate">
                                        {titleByCode.get(e.milestone_code) ?? e.milestone_code}
                                    </span>
                                    <StatusPill status={e.new_status} size="xs" />
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}
